/**
 * Roadmap List Page
 *
 * Displays a list of all roadmaps as cards.
 * Click on a card to navigate to the Gantt view.
 */

import { useRoadmaps } from '@medsuite/api-client';
import { useNavigate } from 'react-router-dom';

/**
 * RoadmapListPage Component
 *
 * Features:
 * - Grid of roadmap cards
 * - Each card shows: title, company name, context preview, theme/item counts
 * - Click card to navigate to /roadmap/:id
 */
export function RoadmapListPage() {
  const navigate = useNavigate();
  const { data: roadmaps, isLoading, error } = useRoadmaps();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Product Roadmaps
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                View and manage product roadmaps with timeline visualization
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {error ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg
                className="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Error loading roadmaps
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
          </div>
        ) : isLoading ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-500"></div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading roadmaps...</p>
          </div>
        ) : !roadmaps || roadmaps.length === 0 ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
              />
            </svg>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
              No roadmaps yet
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Create a roadmap in the Admin section to get started
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Go to Admin
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {roadmaps.map((roadmap) => (
              <div
                key={roadmap.roadmap_id}
                onClick={() => navigate(`/roadmap/${roadmap.roadmap_id}`)}
                className="group cursor-pointer overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow hover:shadow-lg transition-all duration-200"
              >
                <div className="p-6">
                  {/* Company Badge */}
                  <div className="mb-3">
                    <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/50 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-200">
                      {roadmap.company_name}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {roadmap.roadmap_title}
                  </h3>

                  {/* Context Preview */}
                  {roadmap.roadmap_context && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                      {roadmap.roadmap_context}
                    </p>
                  )}

                  {/* Stats */}
                  <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <svg
                          className="mr-1.5 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 01-1.125-1.125v-3.75zM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-8.25zM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-2.25z"
                          />
                        </svg>
                        {String((roadmap as unknown as Record<string, unknown>).theme_count ?? 0)} themes
                      </span>
                      <span className="flex items-center">
                        <svg
                          className="mr-1.5 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                          />
                        </svg>
                        {String((roadmap as unknown as Record<string, unknown>).item_count ?? 0)} items
                      </span>
                    </div>
                    <svg
                      className="h-5 w-5 text-gray-400 dark:text-gray-600 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8.25 4.5l7.5 7.5-7.5 7.5"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default RoadmapListPage;
